// Where the photos go on a page. One function, every renderer.
//
// ─── WHY THIS FILE EXISTS ───────────────────────────────────────────────────
//
// Until 2026-09-07 the slot maths lived inside pdfRenderer.ts, a file that
// imports resvg WASM and therefore cannot be loaded by any test. Nothing could
// ask it "what do you draw for layout X?" — and so nothing noticed that for
// `3-up-flip` the answer was `undefined`. The editor and web/lib/pdfGenerator.ts
// had shipped that layout long before; the print renderer had no case for it
// and no default, and its own type union did not list it, so the switch looked
// exhaustive to the compiler. Twelve pages across seven albums were stored with
// a layout the printer could not draw.
//
// ⚠️ THE MATHS IS HERE, DEPENDENCY-FREE, SO IT CAN BE TESTED AND SHARED.
// pdfRenderer calls `slotRects`; nothing else in that file knows a layout name.
//
// ─── COORDINATES ────────────────────────────────────────────────────────────
//
// PDF space: origin bottom-left, y grows UP (pdf-lib). A rect's `y` is its
// bottom edge. "Top" slots therefore have the LARGER y. Callers rendering in a
// y-down space (CSS) flip with `H - y - h`.
//
// Guard: src/lib/slotGeometry.test.ts

/**
 * Every layout a page can carry.
 *
 * ⚠️ THIS IS THE ONE LIST. pdfRenderer's `LayoutPage.layout` is typed from it.
 * Adding a layout anywhere else and not here reproduces the `3-up-flip` hole:
 * the compiler stays quiet and a page prints blank.
 */
export const LAYOUTS = ['1-up', '2-up', '3-up', '4-up', 'wide-top', '3-up-flip', '2-up-side', '5-up', '6-up', '4-up-main', '2-up-even'] as const;
export type Layout = typeof LAYOUTS[number];

/** 5-up's top band (one big landscape), as a fraction of page height. */
export const FIVE_UP_TOP_FRAC = 0.5;
/** 4-up-main's top band (design system M4-2 « main + 3 small »). */
export const FOUR_MAIN_TOP_FRAC = 0.58;

/**
 * 2-up-even: two landscapes full width at their own shape, the page's spare
 * height shared evenly above, between and below them (owner, 2026-10-12: « no
 * white bars — if two landscapes leave empty space, the space is even »).
 * `top_h_frac` carries each photo's height as a fraction of the page's inside.
 * Never so tall that the space between them is under the gap.
 */
export function evenFrac(topFrac: number | null | undefined, H: number, gap: number): number {
  const max = H > 0 ? (1 - 3 * Math.max(0, gap) / H) / 2 : 0.5;
  return Math.max(0.1, Math.min(max, Number(topFrac) || 0.4));
}

/** Wide-top band limits, as a fraction of page height. */
export const WIDE_TOP_MIN_FRAC = 70 / 297;
export const WIDE_TOP_MAX_FRAC = 200 / 297;

export interface Rect { x: number; y: number; w: number; h: number }

// ─── BASE LAYOUT RULES (owner, 2026-10-12 — « Storiz — Album design system ») ──
//
// « Outer margin 12 mm, spine side 15 mm. Replaces the edge-to-edge rule.
// Space between photos: 4 mm, the same everywhere. » An album laid out under
// these rules carries `album_sessions.layout_rules = 'base-v1'`; every renderer
// asks `baseFrameMm` for the page's frame and hands it to `slotRects`. An album
// without the stamp gets NO frame — byte-identical to before.
//
// ⚠️ THE STAMP, NOT A GLOBAL SWITCH, DECIDES THE GEOMETRY. A global switch read
// at render time would move the photos of every album already laid out for
// edge-to-edge pages — their crops were computed for the old slot sizes.

export const BASE_RULES_V1 = 'base-v1';
export const BASE_MARGIN_MM = 12;
export const BASE_SPINE_MM = 15;
export const BASE_GAP_MM = 4;
/** Faces and text at least this far from the trim. Inside the 12 mm margin by design. */
export const BASE_SAFE_MM = 6;

/** Outer margins of one page, in the caller's units. */
export interface PageFrame { left: number; right: number; top: number; bottom: number }
export const NO_FRAME: PageFrame = { left: 0, right: 0, top: 0, bottom: 0 };

/** True when the album was laid out under the base rules. */
export function usesBaseRules(layoutRules: unknown): boolean {
  return layoutRules === BASE_RULES_V1;
}

/**
 * The frame of album page `pageIndex` (0-based, the editor's order) in mm.
 *
 * Page 1 of the album is a LEFT page — the printed book opens on the intro
 * page on the right — so even indexes are left pages, whose spine is on their
 * RIGHT; odd indexes are right pages, spine on their LEFT. 12 + 15 = 27 mm on
 * every page, so a slot's SIZE never depends on the side — only its offset.
 */
export function baseFrameMm(pageIndex: number): PageFrame {
  const left = Number.isFinite(pageIndex) && Math.abs(pageIndex) % 2 === 0;
  return {
    left: left ? BASE_MARGIN_MM : BASE_SPINE_MM,
    right: left ? BASE_SPINE_MM : BASE_MARGIN_MM,
    top: BASE_MARGIN_MM,
    bottom: BASE_MARGIN_MM,
  };
}

/** `frame` scaled from mm to the caller's units (`unitsPerMm`). */
export function scaleFrame(frame: PageFrame, unitsPerMm: number): PageFrame {
  return {
    left: frame.left * unitsPerMm, right: frame.right * unitsPerMm,
    top: frame.top * unitsPerMm, bottom: frame.bottom * unitsPerMm,
  };
}

/**
 * Slot rectangles for one page, in the caller's units, y-up.
 *
 * `gap` is the space between ADJACENT photos, in the same units as W/H.
 * It is subtracted from the shared dimension and offset between neighbours.
 * It never touches the outer edges — those stay flush so `withBleed` can
 * extend them past the trim.
 *
 * ⚠️ NEVER RETURNS NOTHING. An unknown layout comes back as a full-page 1-up
 * and a console error. Wrong is visible on the preview a customer approves;
 * empty was not.
 */
export function slotRects(
  layout: string,
  topFrac: number | null | undefined,
  W: number,
  H: number,
  gap: number,
  /** Base-rules margins (y-up: `bottom` is the low edge). Absent = edge to edge. */
  frame?: PageFrame | null,
): Rect[] {
  if (frame && (frame.left || frame.right || frame.top || frame.bottom)) {
    const inner = slotRects(layout, topFrac, W - frame.left - frame.right, H - frame.top - frame.bottom, gap);
    return inner.map((r) => ({ x: r.x + frame.left, y: r.y + frame.bottom, w: r.w, h: r.h }));
  }
  const G = Math.max(0, Number.isFinite(gap) ? gap : 0);
  switch (layout as Layout) {
    case '1-up':
      return [{ x: 0, y: 0, w: W, h: H }];

    case '2-up-even': {
      const h = evenFrac(topFrac, H, G) * H;
      const s = (H - 2 * h) / 3;
      return [
        { x: 0, y: 2 * s + h, w: W, h },
        { x: 0, y: s, w: W, h },
      ];
    }

    case 'wide-top': {
      const f = Math.max(WIDE_TOP_MIN_FRAC, Math.min(WIDE_TOP_MAX_FRAC, topFrac ?? 0.5));
      const topH = f * H - G / 2;
      const botH = (1 - f) * H - G / 2;
      return [
        { x: 0, y: botH + G, w: W, h: topH },
        { x: 0, y: 0, w: W, h: botH },
      ];
    }

    case '2-up': {
      const h = (H - G) / 2;
      return [
        { x: 0, y: h + G, w: W, h },
        { x: 0, y: 0, w: W, h },
      ];
    }

    case '3-up': {
      const topH = H * 0.6 - G / 2;
      const botH = H * 0.4 - G / 2;
      const halfW = (W - G) / 2;
      return [
        { x: 0, y: botH + G, w: W, h: topH },
        { x: 0, y: 0, w: halfW, h: botH },
        { x: halfW + G, y: 0, w: halfW, h: botH },
      ];
    }

    case '4-up': {
      const halfW = (W - G) / 2;
      const halfH = (H - G) / 2;
      return [
        { x: 0, y: halfH + G, w: halfW, h: halfH },
        { x: halfW + G, y: halfH + G, w: halfW, h: halfH },
        { x: 0, y: 0, w: halfW, h: halfH },
        { x: halfW + G, y: 0, w: halfW, h: halfH },
      ];
    }

    // Two half-width portraits on top, one full-width photo below. Copied
    // from web/lib/pdfGenerator.ts, which had it all along.
    case '3-up-flip': {
      const halfW = (W - G) / 2;
      const halfH = (H - G) / 2;
      return [
        { x: 0, y: halfH + G, w: halfW, h: halfH },
        { x: halfW + G, y: halfH + G, w: halfW, h: halfH },
        { x: 0, y: 0, w: W, h: halfH },
      ];
    }

    // Two portraits side by side, full height (base rules: « portraits in
    // pairs, side by side »). Slot 0 left, slot 1 right.
    case '2-up-side': {
      const halfW = (W - G) / 2;
      return [
        { x: 0, y: 0, w: halfW, h: H },
        { x: halfW + G, y: 0, w: halfW, h: H },
      ];
    }

    // 5-up (base rules, design system M5-1 « main + 4 small »): one big
    // landscape across the top half, four below in two rows of two. Slot 0 =
    // the main photo. Rows below share what the band leaves, equally.
    case '5-up': {
      const topH = FIVE_UP_TOP_FRAC * H - G / 2;
      const rowH = (H - topH - 2 * G) / 2;
      const halfW = (W - G) / 2;
      return [
        { x: 0, y: H - topH, w: W, h: topH },
        { x: 0, y: rowH + G, w: halfW, h: rowH },
        { x: halfW + G, y: rowH + G, w: halfW, h: rowH },
        { x: 0, y: 0, w: halfW, h: rowH },
        { x: halfW + G, y: 0, w: halfW, h: rowH },
      ];
    }

    // 4-up-main (base rules, design system M4-2 « main + 3 small »): one big
    // landscape across the top, three portraits side by side below. Slot 0 =
    // the main photo.
    case '4-up-main': {
      const topH = FOUR_MAIN_TOP_FRAC * H - G / 2;
      const botH = H - topH - G;
      const w3 = (W - 2 * G) / 3;
      return [
        { x: 0, y: botH + G, w: W, h: topH },
        { x: 0, y: 0, w: w3, h: botH },
        { x: w3 + G, y: 0, w: w3, h: botH },
        { x: 2 * (w3 + G), y: 0, w: w3, h: botH },
      ];
    }

    // 6-up (base rules, design system M6-1 « many small moments »): two
    // columns × three rows of near-square cells — portrait or landscape.
    case '6-up': {
      const halfW = (W - G) / 2;
      const rowH = (H - 2 * G) / 3;
      const out: Rect[] = [];
      for (let r = 0; r < 3; r += 1) {
        // Counted from the bottom so the last row sits on y = 0 exactly
        // (withBleed tests `=== 0` to know a slot touches the trim).
        const y = (2 - r) * (rowH + G);
        out.push({ x: 0, y, w: halfW, h: rowH }, { x: halfW + G, y, w: halfW, h: rowH });
      }
      return out;
    }

    default:
      console.error(`[slotGeometry] unknown layout "${layout}" — falling back to 1-up`);
      return [{ x: 0, y: 0, w: W, h: H }];
  }
}

/**
 * Push every slot that touches a page edge out into the bleed zone.
 *
 * The page being drawn is (W + 2·bleed) × (H + 2·bleed); slots come in
 * trim-relative and go out bleed-page-relative. Interior edges — and any gap
 * between photos — are untouched: the blade never reaches them.
 */
export function withBleed(rects: Rect[], W: number, H: number, bleed: number): Rect[] {
  if (bleed === 0) return rects;
  return rects.map((slot) => {
    let x = slot.x + bleed;
    let y = slot.y + bleed;
    let w = slot.w;
    let h = slot.h;
    if (slot.x === 0) { x = 0; w += bleed; }
    if (slot.x + slot.w >= W - 0.5) { w += bleed; }
    if (slot.y === 0) { y = 0; h += bleed; }
    if (slot.y + slot.h >= H - 0.5) { h += bleed; }
    return { x, y, w, h };
  });
}
